import { _decorator } from 'cc';
import NumberPrecision from 'number-precision';
import ColorLevelAdjuster from '../../../Components/Shader/ColorLevelAdjuster';
import RangeSlider from '../../../Components/ValuePicker/RangeSlider';
import BaseColorAdjusterView, { IBaseColorAdjusterInfo } from './BaseColorAdjusterView';

interface IColorLevelAdjusterInfo extends IBaseColorAdjusterInfo {
    inputBlack: number;
    inputWhite: number;
    gamma: number;
    outputBlack: number;
    outputWhite: number;
}

const { ccclass, property } = _decorator;

@ccclass('ColorLevelAdjusterView')
export default class ColorLevelAdjusterView extends BaseColorAdjusterView<ColorLevelAdjuster> {
    @property(RangeSlider)
    private readonly inputBlack: RangeSlider = null;

    @property(RangeSlider)
    private readonly inputWhite: RangeSlider = null;

    @property(RangeSlider)
    private readonly gamma: RangeSlider = null;

    @property(RangeSlider)
    private readonly outputBlack: RangeSlider = null;

    @property(RangeSlider)
    private readonly outputWhite: RangeSlider = null;

    private _isSyncing: boolean = false;

    public onLoad() {
        super.onLoad();
        this.inputBlack.on(RangeSlider.EVENT_TYPE.PROGRESS_CHANGED, this.apply, this);
        this.inputWhite.on(RangeSlider.EVENT_TYPE.PROGRESS_CHANGED, this.apply, this);
        this.gamma.on(RangeSlider.EVENT_TYPE.PROGRESS_CHANGED, this.apply, this);
        this.outputBlack.on(RangeSlider.EVENT_TYPE.PROGRESS_CHANGED, this.apply, this);
        this.outputWhite.on(RangeSlider.EVENT_TYPE.PROGRESS_CHANGED, this.apply, this);
        this.gamma.progressValueMapping = (progress: number) => {
            const mid = 1.0;
            let v = 1.0;
            if (progress < 0.5) {
                v = 2 * progress * (mid - this.gamma.max) + this.gamma.max;
            } else if (progress > 0.5) {
                v = 2 * (progress - 0.5) * (this.gamma.min - mid) + mid;
            } else {
                v = 1.0;
            }
            // precision safe modulo
            let modulo = v;
            while (modulo >= this.gamma.step) {
                modulo = NumberPrecision.minus(modulo, this.gamma.step);
            }
            return NumberPrecision.minus(v, modulo);
        };
    }

    public collectAdjustInfo(): IColorLevelAdjusterInfo {
        const target = this.currentTarget;
        if (!target) {
            return null;
        }
        return {
            name: 'colorLevel',
            inputBlack: target.inputBlack,
            inputWhite: target.inputWhite,
            gamma: target.gamma,
            outputBlack: target.outputBlack,
            outputWhite: target.outputWhite,
        };
    }

    protected apply() {
        if (this._isSyncing) {
            return;
        }
        const width = this.inputBlack.node.transformWidth;
        this.gamma.node.transformWidth = (this.inputWhite.progress - this.inputBlack.progress) * width;
        this.gamma.node.setPosition(((this.inputWhite.progress + this.inputBlack.progress - 1) / 2) * width, this.gamma.node.y, 0);
        const target = this.currentTarget;
        if (!target) {
            return;
        }
        target.inputBlack = this.inputBlack.value;
        target.inputWhite = this.inputWhite.value;
        target.gamma = this.gamma.value;
        target.outputBlack = this.outputBlack.value;
        target.outputWhite = this.outputWhite.value;
    }

    protected syncUIWithTarget() {
        const target = this.currentTarget;
        if (!target) {
            return;
        }
        this._isSyncing = true;
        this.inputBlack.progress = target.inputBlack / 255;
        this.inputWhite.progress = target.inputWhite / 255;
        // TODO map actual progress from gamma value
        this.gamma.progress = 0.5;
        this.outputBlack.progress = target.outputBlack / 255;
        this.outputWhite.progress = target.outputWhite / 255;
        const width = this.inputBlack.node.transformWidth;
        this.gamma.node.transformWidth = (this.inputWhite.progress - this.inputBlack.progress) * width;
        this.gamma.node.setPosition(((this.inputWhite.progress + this.inputBlack.progress - 1) / 2) * width, this.gamma.node.y, 0);
        this._isSyncing = false;
    }

    protected reset() {
        this.inputBlack.progress = 0;
        this.inputWhite.progress = 1;
        this.gamma.progress = 0.5;
        this.outputBlack.progress = 0;
        this.outputWhite.progress = 1;
    }
}
